import { http, HttpResponse } from "msw";

import { type SavedFilter } from "@vega-api/generated/types/savedFilter";
import { type SavedFilterRequest } from "@vega-api/generated/types/savedFilterRequest";
import { type UpdateSavedFilterRequest } from "@vega-api/generated/types/updateSavedFilterRequest";

const QUICK_LINKS_TABLE_CODE_PREFIX = "gp-home.quickAccess";
const QUICK_LINKS_ROW_ID = "11111111-2222-3333-4444-555555555555";
const QUICK_LINKS_STORAGE_KEY = "msw-demo.gp-home.quickAccess";

// sessionStorage-backed store so the GP Home quick-links lifecycle (add / edit
// / remove / reorder) works end to end in the MSW demo mode and survives page
// reloads (browser resolvers run in the page context, so module state alone
// would reset). Handlers fall through (return nothing) for every other
// saved-filters table code.
const readQuickLinksRow = (): SavedFilter | undefined => {
  const stored = sessionStorage.getItem(QUICK_LINKS_STORAGE_KEY);

  return stored === null ? undefined : (JSON.parse(stored) as SavedFilter);
};

const writeQuickLinksRow = (row: SavedFilter): SavedFilter => {
  sessionStorage.setItem(QUICK_LINKS_STORAGE_KEY, JSON.stringify(row));

  return row;
};

export const quickLinksDemoHandlers = [
  http.get(
    "*/api/authentication/v1/filters/by-table-code/:tableCode",
    ({ params }) => {
      if (!String(params.tableCode).startsWith(QUICK_LINKS_TABLE_CODE_PREFIX)) {
        return;
      }

      const row = readQuickLinksRow();

      return HttpResponse.json({ data: row ? [row] : [] });
    },
  ),
  http.post("*/api/authentication/v1/filters", async ({ request }) => {
    const body = (await request.clone().json()) as SavedFilterRequest;

    if (!body.tableCode.startsWith(QUICK_LINKS_TABLE_CODE_PREFIX)) {
      return;
    }

    const row = writeQuickLinksRow({
      id: QUICK_LINKS_ROW_ID,
      filterName: body.filterName,
      filterConfig: body.filterConfig,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });

    return HttpResponse.json({ data: row });
  }),
  http.put(
    "*/api/authentication/v1/filters/:filterId",
    async ({ params, request }) => {
      if (params.filterId !== QUICK_LINKS_ROW_ID) {
        return;
      }

      const body = (await request.clone().json()) as UpdateSavedFilterRequest;

      const row = writeQuickLinksRow({
        ...readQuickLinksRow(),
        filterName: body.filterName,
        filterConfig: body.filterConfig,
        updatedAt: new Date().toISOString(),
      });

      return HttpResponse.json({ data: row });
    },
  ),
];
